import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  PackageCheck,
  Building2,
} from "lucide-react";
import { formatNumber, formatMoney } from "@/lib/utils";

import { toVnDateString } from "@apv-erp/status";
interface OutsourceReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  outsourcePO: any | null;
  onSuccess?: () => void;
}

export function OutsourceReceiptModal({
  isOpen,
  onClose,
  outsourcePO,
  onSuccess,
}: OutsourceReceiptModalProps) {
  const [receivedQuantity, setReceivedQuantity] = useState<number>(0);
  const [qtyAccepted, setQtyAccepted] = useState<number>(0);
  const [qtyRejected, setQtyRejected] = useState<number>(0);
  const [defectReason, setDefectReason] = useState<string>("");
  const [receiptDate, setReceiptDate] = useState<string>(
    toVnDateString()
  );
  const [notes, setNotes] = useState<string>("");
  // Mặc định không tích: hệ thống chưa quản kho bán thành phẩm, hướng dẫn yêu cầu hỏi quản trị trước khi tích
  const [autoReceiveToWip, setAutoReceiveToWip] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Giai đoạn 2: Bù trừ đền bù phế phẩm (Debit Note) & Dung sai thiếu hụt
  const [isCompensationEnabled, setIsCompensationEnabled] = useState<boolean>(false);
  const [compensationAmount, setCompensationAmount] = useState<number>(0);
  const [compensationReason, setCompensationReason] = useState<string>("");
  const [isToleranceAccepted, setIsToleranceAccepted] = useState<boolean>(false);

  const remainingToReceive = outsourcePO
    ? Math.max(
        0,
        Math.max(outsourcePO.qtySentTotal || 0, (outsourcePO.qtyOrdered || 0) + (outsourcePO.allowanceQty || 0)) -
          (outsourcePO.qtyReceived || 0),
      )
    : 0;

  useEffect(() => {
    if (outsourcePO) {
      const targetSent = Math.max(
        outsourcePO.qtySentTotal || 0,
        (outsourcePO.qtyOrdered || 0) + (outsourcePO.allowanceQty || 0),
      );
      const alreadyReceived = outsourcePO.qtyReceived || 0;
      const remaining = Math.max(0, targetSent - alreadyReceived);

      // hết số còn lại thì không gợi ý nhận thêm (trước đây gợi ý lại cả SL đặt → nhận trùng, tiến độ 200%)
      const defaultQty = remaining;
      setReceivedQuantity(defaultQty);
      setQtyAccepted(defaultQty);
      setQtyRejected(0);
      setDefectReason("");
      setReceiptDate(toVnDateString());
      setNotes("");
      setAutoReceiveToWip(false);
      setIsCompensationEnabled(false);
      setCompensationAmount(0);
      setCompensationReason("");
      setIsToleranceAccepted(false);
    }
  }, [outsourcePO]);

  if (!outsourcePO) return null;

  const targetSent =
    outsourcePO.qtySentTotal ||
    outsourcePO.qtyOrdered + (outsourcePO.allowanceQty || 0);
  const alreadyReceived = outsourcePO.qtyReceived || 0;

  const totalDeliveredSoFar = (alreadyReceived || 0) + (qtyAccepted || 0);
  const isShort = totalDeliveredSoFar < targetSent;
  const shortageQty = Math.max(0, targetSent - totalDeliveredSoFar);
  const shortagePercent = targetSent > 0 ? (shortageQty / targetSent) * 100 : 0;
  const isWithinTolerance = shortagePercent <= 5;

  const totalOrderValue = Number(outsourcePO.qtyOrdered || 0) * Number(outsourcePO.unitPrice || 0);
  const feeAmount = (qtyAccepted || 0) * Number(outsourcePO.unitPrice || 0);
  const compAmount = isCompensationEnabled ? Number(compensationAmount || 0) : 0;
  const isAbnormalCompensation = isCompensationEnabled && compAmount > 0 && totalOrderValue > 0 && compAmount > totalOrderValue * 3;
  const netPayable = Math.max(0, feeAmount - compAmount);

  const handleTotalChange = (val: number) => {
    setReceivedQuantity(val);
    const rej = qtyRejected || 0;
    setQtyAccepted(Math.max(0, val - rej));
  };

  const handleRejectedChange = (val: number) => {
    setQtyRejected(val);
    const total = receivedQuantity || 0;
    setQtyAccepted(Math.max(0, total - val));
  };

  const handleConfirm = async () => {
    if (!receivedQuantity || receivedQuantity <= 0) {
      toast.error("Vui lòng nhập số lượng nhận hồi xưởng hợp lệ (> 0)");
      return;
    }

    if (qtyAccepted + qtyRejected !== receivedQuantity) {
      toast.error(
        "Tổng số lượng Đạt và Lỗi phải bằng tổng số lượng nhận thực tế!"
      );
      return;
    }

    if (compAmount > 500_000_000) {
      toast.error("Số tiền đền bù không được vượt quá 500.000.000 VNĐ!");
      return;
    }

    if (compAmount > 0 && (!compensationReason || compensationReason.trim().length < 3)) {
      toast.error("Vui lòng nhập lý do khấu trừ đền bù khi có phát sinh tiền đền bù (tối thiểu 3 ký tự)!");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await trpcClient.outsourcePos.recordBatchReceipt.mutate({
        id: outsourcePO.id,
        receiptDate,
        receivedQuantity: Number(receivedQuantity),
        qtyAccepted: Number(qtyAccepted),
        qtyRejected: Number(qtyRejected),
        defectReason: defectReason || undefined,
        notes: notes || undefined,
        autoReceiveToWip,
        compensationAmount: compAmount > 0 ? compAmount : undefined,
        compensationReason: compAmount > 0 ? (compensationReason || defectReason || "Đền bù phế phẩm") : undefined,
        isToleranceAccepted: isShort && isWithinTolerance && isToleranceAccepted,
      });

      if ((res as any)?.warning) toast.warning((res as any).warning, { duration: 10000 });
      const docMsg = res?.inventoryDocNo
        ? ` (Đã tạo phiếu nhập kho: ${res.inventoryDocNo})`
        : "";
      const debitMsg = compAmount > 0
        ? ` [Đã trừ đền bù: -${formatMoney(compAmount)}]`
        : "";

      toast.success(
        `Nghiệm thu nhận ${formatNumber(receivedQuantity)} pcs (Đạt: ${formatNumber(qtyAccepted)}, Lỗi: ${formatNumber(qtyRejected)})${debitMsg} thành công!${docMsg}`
      );

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi ghi nhận nghiệm thu hàng gia công");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <PackageCheck className="h-5 w-5 text-emerald-600" />
            Nghiệm Thu Nhận BTP Gia Công Về Xưởng
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {/* Summary Box */}
          <div className="rounded-lg bg-slate-50 p-3 border border-slate-200 space-y-1.5">
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Mã đơn GC:</span>
              <span className="font-mono font-medium text-indigo-600">
                {outsourcePO.poId}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Đơn vị gia công:</span>
              <span className="font-medium text-slate-700">
                {outsourcePO.vendor?.name || outsourcePO.vendor?.code || "—"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 text-xs">Sản phẩm / Công đoạn:</span>
              <span className="font-medium text-slate-700 text-right max-w-[260px] break-words whitespace-normal">
                {outsourcePO.productName || outsourcePO.productSpecs || "BTP"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-200 text-xs">
              <div>
                <span className="text-slate-500">Kế hoạch xuất: </span>
                <span className="font-semibold">{formatNumber(targetSent)}</span>
              </div>
              <div>
                <span className="text-slate-500">Đã nhận trước: </span>
                <span className="font-semibold text-blue-600">
                  {formatNumber(alreadyReceived)}
                </span>
              </div>
              <div className="text-right">
                <span className="text-slate-500">Đơn giá: </span>
                <span className="font-semibold text-emerald-600">
                  {formatMoney(outsourcePO.unitPrice)}
                </span>
              </div>
            </div>
          </div>

          {/* Quantity Inputs Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Tổng nhận đợt này <span className="text-rose-500">*</span>
              </Label>
              <Input
                type="number"
                min={1}
                value={receivedQuantity || ""}
                onChange={(e) => handleTotalChange(Number(e.target.value))}
                className="mt-1 font-semibold"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-emerald-700">
                Số lượng ĐẠT (OK)
              </Label>
              <Input
                type="number"
                min={0}
                value={qtyAccepted || ""}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setQtyAccepted(val);
                  setQtyRejected(Math.max(0, receivedQuantity - val));
                }}
                className="mt-1 font-semibold text-emerald-700 border-emerald-300 bg-emerald-50/40"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-rose-700">
                Số lượng LỖI (NG)
              </Label>
              <Input
                type="number"
                min={0}
                value={qtyRejected || ""}
                onChange={(e) => handleRejectedChange(Number(e.target.value))}
                className="mt-1 font-semibold text-rose-700 border-rose-300 bg-rose-50/40"
              />
            </div>
          </div>

          {/* Defect Reason Warning if NG > 0 */}
          {qtyRejected > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                Phát hiện {formatNumber(qtyRejected)} sản phẩm gia công lỗi / không đạt KCS
              </div>
              <Input
                placeholder="Nguyên nhân lỗi: Rách mép, bong màng, xước nhũ, lệch bế..."
                value={defectReason}
                onChange={(e) => setDefectReason(e.target.value)}
                className="text-xs bg-white border-amber-300"
              />

              {/* Tùy chọn Đền bù vật tư / Phạt phế phẩm (Debit Note) */}
              <div className="pt-1 border-t border-amber-200 space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isCompensationEnabled"
                    checked={isCompensationEnabled}
                    onCheckedChange={(checked) => {
                      setIsCompensationEnabled(!!checked);
                      if (!checked) setCompensationAmount(0);
                    }}
                  />
                  <Label
                    htmlFor="isCompensationEnabled"
                    className="text-xs font-semibold text-amber-900 cursor-pointer"
                  >
                    Yêu cầu NCC đền bù hư hỏng vật tư / phế phẩm (Khấu trừ công nợ AP)
                  </Label>
                </div>

                {isCompensationEnabled && (
                  <div className="space-y-2 pl-6">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px] font-semibold text-rose-700">
                          Số tiền đền bù (VNĐ) <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={500000000}
                          value={compensationAmount || ""}
                          onChange={(e) => setCompensationAmount(Number(e.target.value))}
                          placeholder="VD: 500000"
                          className="mt-1 text-xs font-semibold text-rose-700 bg-white border-rose-300"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">
                          Lý do khấu trừ <span className="text-rose-500">*</span>
                        </Label>
                        <Input
                          value={compensationReason}
                          onChange={(e) => setCompensationReason(e.target.value)}
                          placeholder="VD: Hỏng 500 tờ giấy Ivory"
                          className="mt-1 text-xs bg-white"
                        />
                      </div>
                    </div>

                    {isAbnormalCompensation && (
                      <div className="flex items-start gap-2 p-2.5 bg-amber-100 border border-amber-300 rounded-md text-amber-900 text-xs">
                        <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-amber-950">Cảnh báo giá trị đền bù bất thường:</span>
                          <p className="text-[11px] text-amber-800 mt-0.5">
                            Số tiền đền bù ({formatMoney(compAmount)}) đang vượt quá 3 lần tổng giá trị đơn gia công ({formatMoney(totalOrderValue)}). Vui lòng kiểm tra lại trước khi xác nhận!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cảnh báo Dung Sai Thiếu Hụt (Tolerance Handling) */}
          {isShort && (
            <div
              className={`rounded-lg border p-2.5 text-xs space-y-1.5 ${
                isWithinTolerance
                  ? "bg-amber-50/70 border-amber-200 text-amber-900"
                  : "bg-rose-50 border-rose-200 text-rose-900"
              }`}
            >
              <div className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle
                  className={`h-4 w-4 shrink-0 ${
                    isWithinTolerance ? "text-amber-600" : "text-rose-600"
                  }`}
                />
                <span>
                  Thiếu hụt {formatNumber(shortageQty)} pcs ({shortagePercent.toFixed(1)}%) so với kế hoạch xuất gửi
                </span>
              </div>
              <p className="text-[11px] text-slate-600">
                {isWithinTolerance
                  ? "Mức hao hụt nằm trong dung sai cho phép (≤ 5%). Bạn có thể tích chọn đóng lệnh gia công hoặc chờ đợt trả hàng tiếp theo."
                  : "Mức hao hụt vượt quá ngưỡng dung sai cho phép (> 5%). Khuyến nghị liên hệ nhà gia công kiểm tra hoặc lập lệnh in bù."}
              </p>

              {isWithinTolerance && (
                <div className="flex items-center space-x-2 pt-1 border-t border-amber-200">
                  <Checkbox
                    id="isToleranceAccepted"
                    checked={isToleranceAccepted}
                    onCheckedChange={(checked) => setIsToleranceAccepted(!!checked)}
                  />
                  <Label
                    htmlFor="isToleranceAccepted"
                    className="text-xs font-semibold text-emerald-800 cursor-pointer"
                  >
                    Chấp nhận dung sai thực tế &amp; Đánh dấu hoàn tất đơn gia công
                  </Label>
                </div>
              )}
            </div>
          )}

          {/* Date and Financial Flow Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Ngày nhận hàng
              </Label>
              <Input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">
                Tổng phát sinh nợ AP đợt này
              </Label>
              <div className="mt-1 p-2 bg-slate-50 rounded-md border border-slate-200 text-xs space-y-1">
                <div className="flex justify-between text-slate-600">
                  <span>Tiền công:</span>
                  <span className="font-semibold text-emerald-700">
                    +{formatMoney(feeAmount)}
                  </span>
                </div>
                {compAmount > 0 && (
                  <div className="flex justify-between text-rose-600">
                    <span>Trừ đền bù:</span>
                    <span className="font-semibold">
                      -{formatMoney(compAmount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-indigo-700 pt-1 border-t border-slate-200">
                  <span>Thực thanh toán:</span>
                  <span>{formatMoney(netPayable)}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">
              Ghi chú nghiệm thu
            </Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="VD: Kiểm tra ngoại quan đạt chuẩn, BTP đã chuyển qua khâu dán..."
              className="mt-1 text-xs"
            />
          </div>

          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="autoReceiveToWip"
              checked={autoReceiveToWip}
              onCheckedChange={(checked) => setAutoReceiveToWip(!!checked)}
            />
            <Label
              htmlFor="autoReceiveToWip"
              className="text-xs text-slate-600 cursor-pointer"
            >
              Tự động tạo Phiếu Nhập Kho BTP (loại RECEIPT vào KHO-WIP) cho sản phẩm ĐẠT
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting || remainingToReceive <= 0}
            title={remainingToReceive <= 0 ? "Đơn đã nghiệm thu đủ số lượng" : undefined}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Đang nghiệm thu...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                Xác Nhận Nghiệm Thu & Nhập Kho
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
